import type { ReactElement } from 'react';
import type { RegionMasterDto, WellMaster } from '../../domain/types';
import { findRegionField } from '../../domain/wellRegions';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

const WELL_COLUMNS: GroupedColumnDef[] = [
  { id: 'wellKey', label: 'API', header: 'API' },
  { id: 'wellName', label: 'Well name', header: 'Well name' },
  { id: 'field', label: 'Field name', header: 'Field name' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'location', label: 'Location', header: 'Location' },
  { id: 'reservoir', label: 'Reservoir', header: 'Reservoir' }
];

function fieldNameCell(w: WellMaster, activeRegions: readonly RegionMasterDto[]): string {
  const cat = findRegionField(activeRegions, w.region, w.fieldId);
  return cat?.name?.trim() || w.field?.trim() || '—';
}

function wellGroupValue(w: WellMaster, columnId: string, activeRegions: readonly RegionMasterDto[]): string {
  switch (columnId) {
    case 'wellKey':
      return w.wellKey;
    case 'wellName':
      return w.wellName;
    case 'field':
      return fieldNameCell(w, activeRegions);
    case 'region':
      return w.region || '—';
    case 'location': {
      const loc = [w.state, w.country].filter(Boolean).join(', ');
      return loc || '—';
    }
    case 'reservoir':
      return w.reservoirType;
    default:
      return '—';
  }
}

interface WellsGridProps {
  rows: WellMaster[];
  selectedWellId: string | null;
  onSelectWell: (id: string) => void;
  activeRegions: RegionMasterDto[];
  canAddWell?: boolean;
  onAddWell?: () => void;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function WellsGrid({
  rows,
  selectedWellId,
  onSelectWell,
  activeRegions,
  canAddWell,
  onAddWell,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: WellsGridProps): ReactElement {
  function renderWellRow(w: WellMaster): ReactElement {
    return (
      <tr
        className={selectedWellId === w.id ? 'selected' : undefined}
        onClick={() => onSelectWell(w.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectWell(w.id);
          }
        }}
        data-testid="wells-row"
        role="button"
        tabIndex={0}
      >
        <td className="mono-id">{w.wellKey}</td>
        <td>{w.wellName}</td>
        <td>{fieldNameCell(w, activeRegions)}</td>
        <td>{w.region || '—'}</td>
        <td>
          {w.state}
          {w.state && w.country ? ', ' : ''}
          {w.country}
        </td>
        <td>{w.reservoirType}</td>
      </tr>
    );
  }

  return (
    <section className="card requests-grid-card" data-testid="wells-grid">
      <div className="row spread requests-grid-toolbar">
        <h2>Wells</h2>
        {canAddWell && onAddWell ? (
          <button type="button" onClick={onAddWell}>
            Add well
          </button>
        ) : null}
      </div>
      <p className="hint">Click a row to view full details in the side panel.</p>
      {loading && <p className="hint">Loading wells…</p>}
      <GroupedDataGrid<WellMaster>
        columns={WELL_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={(w, col) => wellGroupValue(w, col, activeRegions)}
        renderRow={renderWellRow}
        colSpan={6}
        emptyMessage="No wells in this view."
        groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group. Reorder chips for nested groups. Applies to this page."
      />
      <GridPaginationBar
        idPrefix="wells-grid"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
